class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.qty-input__input');
    this.currentQty = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));
    this.input.addEventListener('focus', QuantityInput.handleFocus);
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handles 'click' events on the quantity input element.
   * @param {object} evt - Event object.
   */

  connectedCallback() {
    this.validateQtyRules();
    this.initOnlineCap();
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  /**
   * Online-sellable cap. Only active when the input carries data-online-qty
   * (PDP via quantity-input-pdp.liquid, and cart lines via quantity-input.liquid
   * once that line's variant has synced in_stock_locations data). Inputs
   * without data-online-qty are left completely untouched.
   *
   * Cap is UX-only: inventoryPolicy = CONTINUE means a direct cart POST can
   * still oversell. A Cart/Checkout Validation Function is the server-side fix.
   */
  initOnlineCap() {
    if (this.input.dataset.onlineQty === undefined) return;

    this.qtyMap = {};
    const mapEl = this.querySelector('[data-qty-map]');
    if (mapEl) {
      try {
        this.qtyMap = JSON.parse(mapEl.textContent);
      } catch (e) {
        this.qtyMap = {};
      }
    }

    this.note = this.querySelector('[data-qty-note]');

    // Clamp while typing (native number input does not enforce max on input).
    this.input.addEventListener('input', this.clampToMax.bind(this));

    // Recompute cap when the variant changes.
    const productInfo = this.closest('.product-info');
    this.variantPicker = productInfo && productInfo.querySelector('variant-picker');
    if (this.variantPicker) {
      this.variantPicker.addEventListener(
        'on:variant:change',
        this.onVariantChange.bind(this)
      );
    }

    this.applyCap();
  }

  /** Online-sellable minus what's already in the cart, floored at 0. */
  effectiveMax() {
    const online = parseInt(this.input.dataset.onlineQty, 10) || 0;
    const inCart = parseInt(this.input.dataset.cartQty, 10) || 0;
    return Math.max(online - inCart, 0);
  }

  /** Sets max / disabled state on the input and +/- buttons, toggles the note. */
  applyCap() {
    const max = this.effectiveMax();
    const min = parseInt(this.input.min, 10) || 1;
    const minus = this.querySelector(".qty-input__btn[name='minus']");
    const plus = this.querySelector(".qty-input__btn[name='plus']");
    const sellable = max >= min;

    if (sellable) {
      this.input.max = max;
      this.input.disabled = false;
      if (plus) plus.disabled = false;
      if (this.note) this.note.hidden = true;
    } else {
      this.input.removeAttribute('max');
      this.input.value = 0;
      this.input.disabled = true;
      if (minus) minus.disabled = true;
      if (plus) plus.disabled = true;
      if (this.note) this.note.hidden = false;
    }
    this.clampToMax();
    this.validateQtyRules();
    this.syncSubmitControls(sellable);
  }

  /**
   * Disables Add to Cart / Buy Now (dynamic checkout) once the online-only
   * qty is exhausted. Only runs for the PDP instance (data-online-cap) --
   * without this, those buttons stay clickable and fall back to submitting
   * with no quantity field (disabled inputs aren't serialized), which
   * Shopify defaults to quantity 1.
   */
  syncSubmitControls(onlineSellable) {
    if (!this.hasAttribute('data-online-cap')) return;

    const form = this.closest('form');
    const addBtn = form && form.querySelector('[name="add"]');
    if (!addBtn) return;

    // Capture the server-rendered availability once, before we touch it,
    // as a fallback for products with no variant-picker (single variant).
    if (addBtn.dataset.nativelyAvailable === undefined) {
      addBtn.dataset.nativelyAvailable = addBtn.disabled ? 'false' : 'true';
    }
    const variantAvailable = this.variantPicker && this.variantPicker.variant
      ? this.variantPicker.variant.available
      : addBtn.dataset.nativelyAvailable === 'true';
    const canSubmit = variantAvailable && onlineSellable;

    addBtn.disabled = !canSubmit;

    const idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.disabled = !canSubmit;

    const dynamicCheckout = form.querySelector('.dynamic-checkout-wrapper');
    if (dynamicCheckout) dynamicCheckout.classList.toggle('is-disabled', !canSubmit);
  }

  clampToMax() {
    const max = this.effectiveMax();
    if (max > 0 && parseInt(this.input.value, 10) > max) this.input.value = max;
  }

  onVariantChange(evt) {
    const variant = evt.detail && evt.detail.variant;
    if (!variant || !(variant.id in this.qtyMap)) {
      this.input.dataset.onlineQty = 0;
      this.input.dataset.cartQty = 0;
      this.applyCap();
      return;
    }
    const data = this.qtyMap[variant.id];
    this.input.dataset.variant = variant.id;
    this.input.dataset.onlineQty = data.online;
    this.input.dataset.cartQty = data.cart;
    // Reset to the minimum when switching into an in-stock variant.
    const min = parseInt(this.input.min, 10) || 1;
    if (this.effectiveMax() > 0 && parseInt(this.input.value, 10) < min) {
      this.input.value = min;
    }
    this.applyCap();
  }

  validateQtyRules() {
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".qty-input__btn[name='minus']");
      buttonMinus.classList.toggle('disabled', this.input.value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".qty-input__btn[name='plus']");
      buttonPlus.classList.toggle('disabled', this.input.value >= max);
    }
  }

  handleClick(evt) {
    if (!evt.target.matches('.qty-input__btn')) return;
    evt.preventDefault();

    this.currentQty = this.input.value;

    this.validateQtyRules()

    if (evt.target.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (this.input.value !== this.currentQty) {
      this.input.dispatchEvent(this.changeEvent);
      this.currentQty = this.input.value;
    }
  }

  /**
   * Handles 'focus' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  static handleFocus(evt) {
    if (window.matchMedia('(pointer: fine)').matches) {
      evt.target.select();
    }
  }

  /**
   * Handles 'keydown' events on the input field.
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    if (evt.key !== 'Enter') return;
    evt.preventDefault();

    if (this.input.value !== this.currentQty) {
      this.input.blur();
      this.input.focus();
      this.currentQty = this.input.value;
    }
  }
}

customElements.define('quantity-input', QuantityInput);
